import { policyNodes } from "@/lib/seo/product-schema";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { AppShell } from "@/components/AppShell";
import { CmsEditProvider } from "@/components/cms/cms-edit";
import { IdentityProvider } from "@/components/identity/IdentityProvider";
import { MetaBootstrap } from "@/components/meta/MetaBootstrap";
import { SeoOverrideSync, useSeoOverrideNavigationSync } from "@/components/seo/SeoOverrideSync";
import { getPublicSeoOverrides } from "@/lib/seo/page-seo-public.functions";
import type { PageSeoOverride } from "@/lib/seo/page-seo";
import { PrivacyBanner } from "@/components/consent/PrivacyBanner";
import { AiReferralTracker } from "@/components/analytics/AiReferralTracker";
import { buildGtmSnippet } from "@/lib/analytics/gtm";
import { isEnforcementEnabled } from "@/lib/consent/enforcement";
import { buildConsentDefaultsSnippet } from "@/lib/analytics/consent-mode";
import { pushExclusionFlag } from "@/lib/analytics/exclusions";


const gtmBootstrapSnippet = buildGtmSnippet(undefined, undefined, isEnforcementEnabled());
const consentDefaultsSnippet = buildConsentDefaultsSnippet();
// Signup 10% discount popup temporarily disabled — replaced by Founding 99 promo.
import { LazySignupPopup as SignupPopup } from "@/components/identity/LazySignupPopup";
import { DeviceContinuity } from "@/components/identity/DeviceContinuity";
import { ReturningReminder } from "@/components/identity/ReturningReminder";
import { IdentityLinkHandler } from "@/components/identity/IdentityLinkHandler";
// import { Founding99Popup } from "@/components/identity/Founding99Popup";


import {
  readPreviewFrameSignals,
  rememberPreviewFrame,
  shouldRedirectToAdmin,
} from "@/lib/admin-host-guard";
import { shouldRedirectToAffiliate } from "@/lib/affiliate-host-guard";



import appCss from "../styles.css?url";
import { LanguageProvider } from "@/lib/i18n/provider";
import { resolveLanguage } from "@/lib/i18n/resolve";
import type { Lang } from "@/lib/i18n/config";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-brand-deep">404</h1>
        <h2 className="mt-4 font-display text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-brand/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Please try again.</p>
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-brand/90"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Language preference is resolved from the cookie on the server so the SSR
  // HTML is already in the visitor's chosen language (no flash of English).
  beforeLoad: async (): Promise<{ lang: Lang }> => ({ lang: await resolveLanguage() }),
  // Admin-managed per-path SEO overrides travel with the SSR payload so the
  // client-rendered <head> uses exactly the same values the server injected.
  loader: async (): Promise<{ seoOverrides: PageSeoOverride[] }> => {
    try {
      const { rows } = await getPublicSeoOverrides();
      return { seoOverrides: rows };
    } catch {
      return { seoOverrides: [] };
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#2B2D42" },
      { name: "format-detection", content: "telephone=no" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "google-site-verification", content: "VQBtV7Yrkgz7qu_f58nJPaEmMljFYJb3pS-cDIhheT4" },
      { name: "google-site-verification", content: "FBBIjTmNZB1hkO3ahLBKLdqrCl9xtpp68m1_sboqVUw" },
      { name: "facebook-domain-verification", content: "li47vgimaoxc6jakwtd8ovhqeg52kw" },
      { title: "TruWe. Research-Backed Wellness Supplements India" },
      {
        name: "description",
        content:
          "Biotech-grade nutraceuticals with Bio-Absorb Matrix delivery support, research-backed doses for sleep, hormones, gut and recovery. FSSAI licensed, GMP-certified, lab tested.",
      },
      { property: "og:title", content: "TruWe. Research-Backed Wellness Supplements India" },
      {
        property: "og:description",
        content:
          "Science-backed wellness with Bio-Absorb Matrix delivery support, magnesium, inositol, liver+gut and kids' gummies. Research-backed doses, lab tested, made in India.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "TruWe" },
      { property: "og:locale", content: "en_IN" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@truwe" },
      { name: "twitter:title", content: "TruWe. Research-Backed Wellness Supplements India" },
      {
        name: "twitter:description",
        content:
          "Science-backed wellness, magnesium, inositol, liver+gut and kids' gummies. Research-backed doses, lab tested, made in India.",
      },
      { property: "og:image", content: "https://truwe.in/og-image.jpg" },
      { property: "og:image:secure_url", content: "https://truwe.in/og-image.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "TruWe. Research-Backed Wellness" },
      { name: "twitter:image", content: "https://truwe.in/og-image.jpg" },
      { name: "twitter:image:alt", content: "TruWe. Research-Backed Wellness" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://cdn.shopify.com" },
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
      },
      // Load as non-render-blocking: media="print" is ignored for screen, then
      // an inline script flips it to "all" once loaded (see scripts below).
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
        media: "print",
        "data-fonts": "poppins",
      } as unknown as { rel: string; href: string },
    ],
    scripts: [
      {
        // Promote non-blocking font stylesheet to active as soon as it loads.
        children:
          "(function(){var l=document.querySelector('link[data-fonts=\"poppins\"]');if(!l)return;var f=function(){l.media='all'};if(l.sheet){f();}else{l.addEventListener('load',f,{once:true});}})();",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://truwe.in/#organization",
              name: "TruWe",
              url: "https://truwe.in",
              logo: {
                "@type": "ImageObject",
                url: "https://truwe.in/__l5e/assets-v1/ede5b935-c43f-48c9-9c92-a6088557696b/truwe-logo-precision-v2.png",
                width: 512,
                height: 512,
              },
              description:
                "Indian biotech wellness brand, research-backed nutraceuticals for performance, balance, recovery and longevity.",
              legalName: "Omkara Nutracueticals Pvt Ltd",
              foundingDate: "2025-08-25",
              areaServed: "IN",
              slogan: "Clinical precision. Functional ingredients. Modern wellness.",
              contactPoint: [{
                "@type": "ContactPoint",
                contactType: "customer support",
                email: "hello@truwe.in",
                availableLanguage: ["English", "Hindi"],
                areaServed: "IN",
              }],
              sameAs: ["https://www.instagram.com/truweofficial/", "https://www.facebook.com/profile.php?id=61591463848208"],
            },
            ...policyNodes(),
          ],
        }),
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://truwe.in/#website",
          name: "TruWe",
          url: "https://truwe.in",
          inLanguage: "en-IN",
          publisher: { "@id": "https://truwe.in/#organization" },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://truwe.in/shop?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // The document language must match the visitor's saved preference in the
  // SSR HTML itself, otherwise assistive tech and crawlers see English for a
  // Hinglish page until hydration patches it.
  let lang: Lang = "en";
  try {
    lang = Route.useRouteContext().lang ?? "en";
  } catch {
    /* shell can render outside a resolved route context (error/prerender) */
  }
  return (
    <html lang={lang === "hi-en" ? "hi-Latn-IN" : "en-IN"} data-lang={lang}>
      <head>
        {/* Google Consent Mode v2 defaults. MUST run before any Google tag.
            All ad/analytics signals default to `denied`. Later updated by
            ConsentBanner / CookieSettings via updateGoogleConsent(). */}
        <script dangerouslySetInnerHTML={{ __html: consentDefaultsSnippet }} />
        {/* Google Tag Manager, production-host gated + single-load guard.
            Snippet is a no-op on localhost, *.lovable.app, admin.truwe.in and
            any non-prod host. Duplicate calls are guarded via
            window.__truweGtmLoaded so a stray second inclusion (e.g. Shopify
            theme + app shell) cannot double-boot GTM. */}
        <script dangerouslySetInnerHTML={{ __html: gtmBootstrapSnippet }} />
        {/* End Google Tag Manager */}
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // On the admin host only admin surfaces are reachable — except the editor
    // previews, which embed real storefront pages in a same-origin iframe.
    // Those must render the storefront, never bounce to /admin.
    const enforce = () => {
      const signals = readPreviewFrameSignals();
      rememberPreviewFrame(signals);
      if (
        shouldRedirectToAdmin({
          host: window.location.hostname,
          path: window.location.pathname,
          preview: signals,
        })
      ) {
        router.navigate({ to: "/admin", replace: true });
        return;
      }
      // The partner host (affiliate.truwe.in) serves partner surfaces only.
      if (
        shouldRedirectToAffiliate({
          host: window.location.hostname,
          path: window.location.pathname,
        })
      ) {
        router.navigate({ to: "/affiliate/login", replace: true });
      }
    };
    enforce();
    // Re-check on client-side navigation so the guard can't be walked past.
    return router.subscribe("onResolved", enforce);
  }, [router]);

  // Keep GTM's `truwe_excluded_path` variable in sync on SPA navigation.
  // The container can't be unloaded once booted, so excluded surfaces
  // (/admin, /auth, /account, /api) are gated by this flag instead.
  useEffect(() => {
    if (typeof window === "undefined") return;
    pushExclusionFlag(window.location.pathname);
    const unsub = router.subscribe("onResolved", () => {
      pushExclusionFlag(window.location.pathname);
    });
    return unsub;
  }, [router]);



  // Capture ?ref=CODE (or ?referral=CODE) from any inbound link and persist it
  // until the first successful order clears it. Runs on initial mount and on
  // every subsequent client-side navigation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let mounted = true;
    void import("@/lib/referral/capture").then(({ captureReferralFromLocation }) => {
      if (!mounted) return;
      captureReferralFromLocation();
      const unsub = router.subscribe("onResolved", () => {
        captureReferralFromLocation();
      });
      // Store cleanup on the closure ref via mounted flag; router.subscribe
      // returns an unsubscribe fn we call from the outer cleanup below.
      (router as unknown as { __refUnsub?: () => void }).__refUnsub = unsub;
    });
    return () => {
      mounted = false;
      const ref = router as unknown as { __refUnsub?: () => void };
      ref.__refUnsub?.();
      ref.__refUnsub = undefined;
    };
  }, [router]);

  const { lang } = Route.useRouteContext();
  const { seoOverrides } = Route.useLoaderData();
  useSeoOverrideNavigationSync(seoOverrides);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider initial={lang}>
      <CmsEditProvider>
      <IdentityProvider>
        <SeoOverrideSync rows={seoOverrides} />
        <MetaBootstrap />
        <AppShell />

        <PrivacyBanner />
        <AiReferralTracker />
        <SignupPopup />
        <DeviceContinuity />
        <IdentityLinkHandler />
        <ReturningReminder />
        {/* <Founding99Popup /> */}
      </IdentityProvider>
      </CmsEditProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}


